'use client';

/**
 * **What a just-committed draft reads as in the row, for the frames before the
 * record catches up** (VALE-1773).
 *
 * ## The gap this exists to fill
 *
 * A popover editor's trigger renders the *draft*, so while the panel is open
 * the row tracks what is being typed. The grid's row is
 * `openIndex === index ? <AttributeFieldControl/> : <AttributeReadOut/>`, and
 * `commitAndClose` fires `onSave` without awaiting it and then closes the
 * editor synchronously — so the very next render swaps the trigger for the
 * read-out, whose `field.display` is still the value the *host's* record prop
 * held before the save.
 *
 * That gap is not a latency: even a fully optimistic `setQueryData` reaches
 * subscribers through TanStack Query's `notifyManager`, which batches on a
 * schedule later than the click handler that closed the editor. So there is no
 * host speed that closes it, which is why the ticket says the defect holds
 * "whether the host commits optimistically or not".
 *
 * The reader therefore saw **new, old, new**: the typed value, a repaint of the
 * previous one, then the committed one. On a slow commit that middle frame
 * reads as the edit having been discarded.
 *
 * ## Why a formatter registry rather than keeping the editor mounted
 *
 * The obvious fix — hold the row "open" with the popover forced shut until the
 * prop lands — was considered and rejected. `openIndex` is not just a render
 * switch: it drives `onEditingChange`, the uncommitted-draft signal the
 * close-guard reads, the `Tab` chain, the value cell's open chrome and the
 * resting cap. Holding it non-null would have made every one of those lie, and
 * forcing the popover shut means lifting `useAttributePopoverCommit` from
 * `defaultOpen` to a controlled `open` — a hook carrying four layered timing
 * fixes (VALE-1617's pointerdown race, VALE-1710's confirm control,
 * VALE-1728's spacing, VALE-1321's `Enter` contract).
 *
 * So instead the grid keeps switching to the read-out exactly as it does today,
 * and hands it the committed value to render until the record catches up. The
 * only thing that needs to exist for that is a way to turn a draft into the
 * string the row shows — which every popover editor's trigger already computes.
 * This module is that computation, named once per kind.
 *
 * ## Bind to the trigger's own formatter, never a second one
 *
 * Each arm below calls **the function the editor's trigger calls**, exported
 * from the editor's own module for this purpose. That is not tidiness: the
 * reader is looking at the trigger's output at the instant the editor closes,
 * so a formatter that differed even slightly would substitute one string for
 * another in front of them — a second flash, in place of the one this removes.
 *
 * Empty-state handling matches each trigger too. A kind returns `null` for "no
 * value", and the read-out renders its own `Set <label>…` placeholder from
 * that, exactly as it does for a record-sourced empty.
 *
 * ## Membership
 *
 * Every kind in {@link POPOVER_EDITOR_KINDS} — the eight whose editor floats
 * over the row rather than filling it, and therefore the eight whose trigger
 * echoes the draft. `attribute-draft-display.test.tsx` derives that list from
 * source and reconciles it against the table below both ways, so a new popover
 * kind fails until it is accounted for.
 *
 * The **inline** kinds (`text`, `money`, `date`, `select`, `boolean`,
 * `multiline`, `number`, `triState`) are out of scope and return `undefined`,
 * which switches the bridge off for that row. Their editors have the same
 * render-order gap, but they were not what VALE-1773 reported and covering them
 * means a display contract for eight more kinds; see that ticket's `Rule set`
 * note.
 */

import type { PostalAddress } from '@/lib/services/matters/types';
import { formatPostalAddress } from '@/lib/services/matters/utils';
import { formatBankAccountSummary } from './attribute-bank-account-editor';
import { formatMarketFigureSummary } from './attribute-market-figure-editor';
import { formatMultiSelectSummary } from './attribute-multi-select-editor';
import { formatNameParts } from './attribute-name-editor';
import { formatPrincipalMembersSummary } from './attribute-principal-members-editor';
import { formatSelectWithCustomSummary } from './attribute-select-with-custom-editor';
import { valueConfirmationSummary } from './attribute-value-confirmation-editor';
import type {
  AttributeBankAccount,
  AttributeDraft,
  AttributeEditor,
  AttributeMarketFigure,
  AttributeNameParts,
  AttributePrincipalMembers,
  AttributeValueConfirmation
} from './attribute-types';

/** `''` is "nothing here", which the read-out renders as its placeholder. */
function orNull(text: string): string | null {
  return text === '' ? null : text;
}

/**
 * The row's text for `draft`, or `undefined` when this kind has no draft
 * formatter and the bridge should stay off.
 *
 * The narrowing casts mirror `attribute-field-control.tsx`'s: `AttributeDraft`
 * carries every kind's shape and `editor.kind` is what decides which one this
 * arm receives, which is not something TypeScript can see on its own.
 */
export function attributeDraftDisplay(
  editor: AttributeEditor,
  draft: AttributeDraft
): string | null | undefined {
  switch (editor.kind) {
    case 'name':
      return orNull(
        formatNameParts(
          typeof draft === 'string'
            ? null
            : (draft as AttributeNameParts | null)
        )
      );

    case 'address':
      return orNull(
        formatPostalAddress(
          typeof draft === 'string'
            ? undefined
            : ((draft as PostalAddress | null) ?? undefined)
        )
      );

    case 'bankAccount':
      return orNull(
        formatBankAccountSummary(
          typeof draft === 'string'
            ? null
            : (draft as AttributeBankAccount | null)
        )
      );

    case 'marketFigure':
      return formatMarketFigureSummary(
        typeof draft === 'string'
          ? null
          : (draft as AttributeMarketFigure | null)
      );

    case 'multiSelect':
      return formatMultiSelectSummary(
        typeof draft === 'string' ? draft : '',
        editor.options
      );

    case 'selectWithCustom':
      return formatSelectWithCustomSummary(
        typeof draft === 'string' ? draft : '',
        editor.options
      );

    case 'principalAndMembers': {
      /*
        The trigger's own gate, not the summary's: `formatPrincipalMembersSummary`
        answers `noneLabel` for an empty value, and the trigger shows the row's
        placeholder instead. Restating the gate here rather than folding it into
        the summary, because the summary is also what the *panel* reads, where
        `noneLabel` is the right answer.
      */
      const value =
        typeof draft === 'string'
          ? null
          : (draft as AttributePrincipalMembers | null);
      const chosen =
        (value?.principal ?? null) !== null ||
        (value?.members ?? []).length > 0;
      return chosen
        ? formatPrincipalMembersSummary(value, editor.options, editor.noneLabel)
        : null;
    }

    case 'valueWithConfirmation':
      return valueConfirmationSummary(
        draft && typeof draft === 'object' && 'confirmed' in draft
          ? (draft as AttributeValueConfirmation)
          : null,
        editor.documents
      );

    default:
      return undefined;
  }
}
